import { IWorkbench, WorkbenchLoader, EventEmitter, MiddlewareService, ToolbarService, MenuService, IWorkbenchSettings, IPlatformSetting, WorkbenchSettings } from '@tencent/workbench';
import {
    MenuReportMiddleware,
    ToolbarCheckedMiddleware,
    ToolbarDisabledMiddleware, ToolbarReportMiddleware,
} from '@framework/workbench/middleware/toolbar-middleware';
import FormBaseMiddleware, {
    FormDisabledMiddleware,
    FormHideMiddleware,
} from '@framework/workbench/middleware/form-middleware';
import { TopMenuDisabledMiddleware, TopMenuHideMiddleware } from '@framework/workbench/middleware/top-menu-middleware';
import { IPcEditorAppServices } from '@src/slide/application/entry/toc/editor/pc/pc-editor-app-services.interface';
import { AppServiceProvider } from '@src/slide/application/app-service-provider';
import { SlideAdapter } from '@framework/workbench/editor-adpater/slide-adapter';
import eventBus, { EventKeys } from '@src/slide/infrastructure/module/independent/event-emitter/instance';
import { FormService } from '@tencent/wb-ext-form';
import { PLATFORM_PC } from '@framework/workbench/const/pc-setting-const';
import { AutoReportMiddleware } from '../../middleware/auto-report-middleware';
import { VipTabReportMiddleWare } from '../../middleware/vip-service-report-middleware';
import { getCacConfigByKey } from '@src/slide/infrastructure/module/dependent/config/tob/app-config';

/**
 * workbench loader基类。抽离BC公用的逻辑
 */
@WorkbenchSettings({ platform: PLATFORM_PC })
export default abstract class BasePcWorkbenchLoader extends WorkbenchLoader {
    protected constructor(
        protected workbench: IWorkbench,
        protected eventEmitter: EventEmitter,
        protected middlewareService: MiddlewareService,
    ) {
        super();
        this.settings.set(this.createSettings());
    }

    /**
     * 初始化开始
     * 全局中间件可在此注册。构造顺序为import顺序，执行顺序为注册顺序（先注册后执行）
     * 中间件没有必要依赖顺序。
     */
    public $onInit() {
        this.initToolbarMiddlewares();
        this.initTopMenuMiddlewares();
    }

    /**
     * 初始化完毕
     */
    public $onReady() {
        // global.dispatchEvent(new Event('resize'));
        this.workbench.open(SlideAdapter, {
            mode: 'stack',
        });
        // setTimeout 等待workbench内dom渲染完成
        setTimeout(() => {
            this.showHeaderBar();
            eventBus.emit(EventKeys.WORKBENCH_IS_MOUNT);
        }, 0);
        // 初始化侧边栏
        this.initRightDrawer();
        this.initRightSideBarFloat();

        this.loadDynamicLoaders();

        this.initReportMiddleware();
    }

    public $afterControllersInit() {
    }

    public $onWorkbenchInitError(error: any) {
        throw (error);
    }

    protected getAppService(): IPcEditorAppServices {
        return AppServiceProvider.getInstance().getAppServices() as IPcEditorAppServices;
    }

    protected hidePreContainer() {
        const placeholderContainer = document.getElementById('placeholder-container');
        placeholderContainer?.remove();
        // window.firstScreenBrandLoading?.hide();
    }

    private showHeaderBar() {
        this.hidePreContainer();
    }

    private initToolbarMiddlewares() {
        this.middlewareService.use(ToolbarDisabledMiddleware, {
            on: ToolbarService,
            callbackNames: ['shouldDisabled'],
        });
        this.middlewareService.use(ToolbarCheckedMiddleware, {
            on: ToolbarService,
            callbackNames: ['shouldCheck'],
        });
    }

    private initTopMenuMiddlewares() {
        FormBaseMiddleware.init(this.getAppService().viewableServiceLayer.selectionService);
        this.middlewareService.use(TopMenuDisabledMiddleware, {
            on: MenuService,
            callbackNames: ['shouldDisabled'],
        });
        this.middlewareService.use(TopMenuHideMiddleware, {
            on: MenuService,
            callbackNames: ['shouldHide'],
        });
    }

    private initFormMiddlewares() {
        this.middlewareService.use(FormDisabledMiddleware, {
            on: FormService,
            callbackNames: ['shouldDisabled'],
        });
        this.middlewareService.use(FormHideMiddleware, {
            on: FormService,
            callbackNames: ['shouldHide'],
        });
    }

    private initReportMiddleware() {
        /** 处理具体业务上报逻辑 */
        this.middlewareService.use(ToolbarReportMiddleware, {
            on: ToolbarService,
            callbackNames: ['onClick'],
        });
        this.middlewareService.use(MenuReportMiddleware, {
            on: MenuService,
            callbackNames: ['onClick'],
        });

        /** workbench 自动上报, 处理统一上报逻辑 */
        this.middlewareService.use<ToolbarService>(AutoReportMiddleware, {
            on: ToolbarService,
            callbackNames: ['onVisibleChange', 'onClick'],
        });
        this.middlewareService.use<MenuService>(AutoReportMiddleware, {
            on: MenuService,
            callbackNames: ['onShow', 'onClick'],
        });
        this.middlewareService.use<ToolbarService>(VipTabReportMiddleWare, {
            on: ToolbarService,
            callbackNames: ['onClick'],
        });
        this.middlewareService.use<MenuService>(VipTabReportMiddleWare, {
            on: MenuService,
            callbackNames: ['onClick'],
        });
    }

    /**
     * 初始化抽屉
     */
    private initRightDrawer() {
        this.initFormMiddlewares();

        Promise.all([
            this.getAppService().editableServiceLayer?.drawerInputManager.promise(),
            this.getAppService().editableServiceLayer?.drawerStateManager.promise(),
        ]).then(([drawerInputManager, drawerStateManager]) => {
            drawerStateManager?.init();
            return { drawerInputManager, drawerStateManager };
        })
            .then(({ drawerInputManager, drawerStateManager }) => {
                // 格式面板侧边栏
                try {
                    const propertyEditorManager = this.getAppService().editableServiceLayer?.propertyEditorManager;
                    propertyEditorManager?.promise().then((propertyEditorManager) => {
                        propertyEditorManager.init({
                            drawerInputManager,
                            drawerStateManager,
                        });
                    });
                } catch (error) {
                    window.log.error(`[loadChunk-error]${error}`);
                }

                try {
                    const animationEditorService = this.getAppService().editableServiceLayer?.animationEditorService;
                    animationEditorService?.promise().then((animationEditorService) => {
                        animationEditorService.init(drawerStateManager);
                    });
                } catch (error) {
                    window.log.error(`[loadChunk-error]${error}`);
                }

                // 其他侧边栏，如选择窗格
                this.getAppService().editableServiceLayer?.selectWindowManager
                    .promise().then((selectWindowManager) => {
                        selectWindowManager.init(drawerStateManager);
                    });

                if (getCacConfigByKey('feature.comment', false)) {
                    this.getAppService().viewableServiceLayer.commentManager.promise().then((commentManager) => {
                        commentManager.init(drawerStateManager);
                    });
                }
            });
    }

    /**
     * 初始化侧边栏
     */
    private initRightSideBarFloat() {
        import(/* webpackChunkName: "drawer" */ '@framework/workbench/drawer/right-side-bar-float').then((module) => {
            const RightSideBarFloat = module.default;
            const instance = new RightSideBarFloat();
            instance.showRightSideBar();
        })
            .catch((error) => {
                window.log.error(`[loadChunk-error]${error}`);
            });
    }

    private loadDynamicLoaders() {
        this.eventEmitter.emit('loadDynamicLoaders');
    }

    /**
     * 创建workbench的settings配置，由子类实现
     * @protected
     */
    protected abstract createSettings(): IWorkbenchSettings<IPlatformSetting>;
}
